import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, Users, TrendingUp, DollarSign, ChevronDown, ChevronRight, Calendar, ArrowUpRight } from 'lucide-react';
import { bousanmSolideApi } from '../services/bousanmSolideApi';

const AgentsBS = () => {
  const { t } = useTranslation(['solide', 'common']);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);

  useEffect(() => {
    bousanmSolideApi.getAllAgentsPortfolio()
      .then(data => setAgents(data.agents || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const globalStats = useMemo(() => {
    const total = agents.reduce((acc, a) => ({
      totalClients: acc.totalClients + (a.statistiques?.total_clients || 0),
      clientsAJour: acc.clientsAJour + (a.statistiques?.clients_a_jour || 0),
      montantCollecteHTG: acc.montantCollecteHTG + (a.statistiques?.montant_total_collecte_htg || 0),
      montantCollecteUSD: acc.montantCollecteUSD + (a.statistiques?.montant_total_collecte_usd || 0)
    }), { totalClients: 0, clientsAJour: 0, montantCollecteHTG: 0, montantCollecteUSD: 0 });
    return { ...total, tauxReussite: total.totalClients > 0 ? (total.clientsAJour / total.totalClients * 100) : 0 };
  }, [agents]);

  if (loading) return <div className="flex h-full items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  const filtered = agents.filter(a => {
    const searchTerm = search.toLowerCase();
    const nom = a.agent?.nom_complet?.toLowerCase() || '';
    const code = a.agent?.compte_agent?.code?.toLowerCase() || '';
    const email = a.agent?.email?.toLowerCase() || '';
    const username = a.agent?.username?.toLowerCase() || '';

    return nom.includes(searchTerm) ||
      code.includes(searchTerm) ||
      email.includes(searchTerm) ||
      username.includes(searchTerm);
  });

  const toggleExpandAgent = (index) => {
    setExpandedAgent(expandedAgent === index ? null : index);
    setExpandedClient(null); // Fermer les clients quand on change d'agent
  };

  const toggleExpandClient = (clientId) => {
    setExpandedClient(expandedClient === clientId ? null : clientId);
  };

  return (
    <div className="h-full w-full bg-gray-50/50 overflow-y-auto p-4">
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{t('solide:agents.title')}</h1>
          <p className="text-xs text-gray-500">{t('solide:agents.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72 group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder={t('solide:agents.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 h-10 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder:text-gray-400"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              >
                <div className="bg-gray-200 hover:bg-gray-300 rounded-full p-0.5 transition-colors">
                  <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:agents.stats.agents')}</p><div className="text-xl font-bold mt-0.5">{agents.length}</div></div>
          <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center"><Users className="h-4 w-4 text-blue-600" /></div>
        </CardContent></Card>
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:agents.stats.clients')}</p><div className="text-xl font-bold mt-0.5">{globalStats.totalClients}</div></div>
          <div className="h-8 w-8 rounded-full bg-purple-50 flex items-center justify-center"><Users className="h-4 w-4 text-purple-600" /></div>
        </CardContent></Card>
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:agents.stats.collected')}</p>
            <div className="flex flex-col mt-0.5">
              <div className="text-sm font-bold">{globalStats.montantCollecteHTG.toLocaleString()} <span className="text-[10px] font-normal text-gray-400">HTG</span></div>
              <div className="text-sm font-bold text-emerald-600">{globalStats.montantCollecteUSD.toLocaleString()} <span className="text-[10px] font-normal text-emerald-400">$</span></div>
            </div>
          </div>
          <div className="h-8 w-8 rounded-full bg-green-50 flex items-center justify-center"><DollarSign className="h-4 w-4 text-green-600" /></div>
        </CardContent></Card>
        <Card className="shadow-sm border-none bg-white"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{t('solide:agents.stats.success')}</p><div className="text-xl font-bold mt-0.5">{globalStats.tauxReussite.toFixed(1)}%</div></div>
          <div className="h-8 w-8 rounded-full bg-orange-50 flex items-center justify-center"><TrendingUp className="h-4 w-4 text-orange-600" /></div>
        </CardContent></Card>
      </div>

      {/* Modern List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-12 gap-4 p-3 bg-gray-50/50 border-b text-xs font-medium text-gray-500">
              <div className="col-span-4 pl-2">{t('solide:agents.table.agent')}</div>
              <div className="col-span-1 text-center">{t('solide:agents.table.clients')}</div>
              <div className="col-span-2 text-right">{t('solide:agents.table.solde')}</div>
              <div className="col-span-2 text-right">{t('solide:agents.table.commission')}</div>
              <div className="col-span-2 text-right">{t('solide:agents.table.collected')}</div>
              <div className="col-span-1 text-center">{t('solide:agents.table.upToDate')}</div>
            </div>

            <div className="divide-y">
              {filtered.map((item, i) => {
                const stats = item.statistiques || {};
                const agent = item.agent || {};
                const isExpanded = expandedAgent === i;

                return (
                  <React.Fragment key={i}>
                    <div
                      className={`grid grid-cols-12 gap-4 p-3 items-center hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}
                      onClick={() => toggleExpandAgent(i)}
                    >
                      <div className="col-span-4 flex items-center gap-3 pl-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600">
                          {agent.nom_complet?.charAt(0) || 'A'}
                        </div>
                        <div className="overflow-hidden flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{agent.nom_complet}</p>
                            {agent.compte_agent?.code && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600 font-medium whitespace-nowrap">
                                {agent.compte_agent.code}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col">
                            {/* <p className="text-[10px] text-gray-500 truncate">@{agent.username}</p> */}
                            {agent.email && <p className="text-[10px] text-gray-400 truncate">{agent.email}</p>}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1 text-center">
                        <Badge variant="secondary" className="font-normal text-xs">{stats.total_clients || 0}</Badge>
                      </div>
                      <div className="col-span-2 text-right text-xs font-medium">
                        <div className="text-blue-600">{(agent.solde_htg || 0).toLocaleString()} <span className="text-[9px] text-blue-400 font-normal">HTG</span></div>
                        <div className="text-emerald-600">{(agent.solde_usd || 0).toLocaleString()} <span className="text-[9px] text-emerald-400 font-normal">USD</span></div>
                      </div>
                      <div className="col-span-2 text-right text-xs font-medium">
                        <div className="text-green-600">{(agent.commission_htg || 0).toLocaleString()} <span className="text-[9px] text-green-400 font-normal">HTG</span></div>
                        <div className="text-emerald-600">{(agent.commission_usd || 0).toLocaleString()} <span className="text-[9px] text-emerald-400 font-normal">USD</span></div>
                      </div>
                      <div className="col-span-2 text-right text-xs font-medium text-gray-900">
                        <div>{(stats.montant_total_collecte_htg || 0).toLocaleString()} <span className="text-[9px] text-gray-400 font-normal">HTG</span></div>
                        <div className="text-emerald-700">{(stats.montant_total_collecte_usd || 0).toLocaleString()} <span className="text-[9px] text-emerald-500 font-normal">USD</span></div>
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-2 pr-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stats.clients_a_jour > stats.clients_en_retard ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {stats.clients_a_jour}/{stats.total_clients}
                        </span>
                        <div className="text-gray-400">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="bg-gray-50 p-4 border-t border-b">
                        <div className="bg-white rounded-md border shadow-sm overflow-hidden">
                          <div className="px-4 py-2 bg-gray-50 border-b flex justify-between items-center">
                            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('solide:agents.details.clientsList')}</h3>
                            <Badge variant="outline" className="bg-white">{t('solide:agents.details.clientsCount', { count: item.clients?.length || 0 })}</Badge>
                          </div>
                          <div className="max-h-96 overflow-y-auto overflow-x-auto">
                            <table className="w-full text-xs text-left min-w-[700px]">
                              <thead className="text-gray-500 bg-gray-50/50 border-b">
                                <tr>
                                  <th className="px-4 py-2 font-medium">{t('agents.details.client')}</th>
                                  <th className="px-4 py-2 font-medium">{t('agents.details.account')}</th>
                                  <th className="px-4 py-2 font-medium text-right">{t('agents.details.savings')}</th>
                                  <th className="px-4 py-2 font-medium text-center">{t('agents.details.progression')}</th>
                                  <th className="px-4 py-2 font-medium text-center">{t('agents.details.status')}</th>
                                  <th className="px-4 py-2"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {(item.clients || []).map((c, j) => {
                                  const isClientOpen = expandedClient === `${i}-${j}`;
                                  const paiements = c.statistiques_paiements || {};
                                  const transactions = c.transactions_recentes?.liste || [];

                                  return (
                                    <React.Fragment key={j}>
                                      <tr
                                        className={`hover:bg-gray-50 cursor-pointer ${isClientOpen ? 'bg-blue-50/30' : ''}`}
                                        onClick={() => toggleExpandClient(`${i}-${j}`)}
                                      >
                                        <td className="px-4 py-3 font-medium text-gray-900">
                                          {c.client?.nom_complet}
                                          <p className="text-[10px] text-gray-500 font-normal">{c.client?.telephone}</p>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{c.compte?.code}</td>
                                        <td className="px-4 py-3 text-right font-medium">
                                          {(c.financier?.solde_solidere || 0).toLocaleString()} <span className="text-[9px] text-gray-400">{c.financier?.devise === 'USD' ? 'USD' : 'HTG'}</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <div className="w-full bg-gray-200 rounded-full h-1.5 max-w-[80px] mx-auto">
                                            <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${c.progression?.pourcentage_completion || 0}%` }}></div>
                                          </div>
                                          <span className="text-[10px] text-gray-400 mt-0.5 block">{c.progression?.pourcentage_completion?.toFixed(0)}%</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <Badge className={`h-5 text-[10px] ${c.situation?.est_a_jour ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
                                            {c.situation?.est_a_jour ? t('solide:clients.table.upToDate') : t('solide:clients.table.late')}
                                          </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                          {isClientOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                                        </td>
                                      </tr>

                                      {/* Client Expanded Details: Transactions & Stats */}
                                      {isClientOpen && (
                                        <tr>
                                          <td colSpan="6" className="p-0 bg-gray-50">
                                            <div className="p-4 border-y border-gray-100">
                                              {/* Stats Paiements - Responsive Grid */}
                                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                                <div className="bg-white p-3 rounded border border-gray-100">
                                                  <p className="text-[10px] text-gray-500 uppercase">{t('solide:agents.details.totalPaid')}</p>
                                                  <p className="text-sm font-bold text-gray-900">{(paiements.montant_total_verse || 0).toLocaleString()} {c.financier?.devise === 'USD' ? '$' : 'G'}</p>
                                                </div>
                                                <div className="bg-white p-3 rounded border border-gray-100">
                                                  <p className="text-[10px] text-gray-500 uppercase">{t('solide:agents.details.transactions')}</p>
                                                  <p className="text-sm font-bold text-gray-900">{paiements.nombre_transactions || 0}</p>
                                                </div>
                                                <div className="bg-white p-3 rounded border border-gray-100">
                                                  <p className="text-[10px] text-gray-500 uppercase">{t('solide:agents.details.average')}</p>
                                                  <p className="text-sm font-bold text-gray-900">{(paiements.moyenne_par_transaction || 0).toLocaleString()} {c.financier?.devise === 'USD' ? '$' : 'G'}</p>
                                                </div>
                                                <div className="bg-white p-3 rounded border border-gray-100">
                                                  <p className="text-[10px] text-gray-500 uppercase">{t('solide:agents.details.lastPayment')}</p>
                                                  <p className="text-sm font-bold text-gray-900">
                                                    {paiements.derniere_transaction_date ? new Date(paiements.derniere_transaction_date).toLocaleDateString() : '-'}
                                                  </p>
                                                </div>
                                              </div>

                                              {/* Transactions Table */}
                                              <div className="bg-white rounded border border-gray-100 overflow-hidden overflow-x-auto">
                                                <div className="px-3 py-2 bg-gray-50 border-b flex items-center gap-2 min-w-[500px]">
                                                  <ArrowUpRight className="h-3 w-3 text-gray-500" />
                                                  <span className="text-xs font-semibold text-gray-700">{t('solide:agents.details.recentTransactions')}</span>
                                                </div>
                                                {transactions.length > 0 ? (
                                                  <table className="w-full text-xs min-w-[500px]">
                                                    <thead className="bg-gray-50/50 text-gray-500">
                                                      <tr>
                                                        <th className="px-3 py-2 text-left font-medium">{t('solide:clients.transactions.date')}</th>
                                                        <th className="px-3 py-2 text-left font-medium">{t('solide:clients.transactions.type')}</th>
                                                        <th className="px-3 py-2 text-right font-medium">{t('solide:clients.transactions.amount')}</th>
                                                        <th className="px-3 py-2 text-right font-medium">{t('solide:clients.transactions.doneBy')}</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody className="divide-y">
                                                      {transactions.map((t, k) => (
                                                        <tr key={k}>
                                                          <td className="px-3 py-2 text-gray-600 flex items-center gap-2">
                                                            <Calendar className="h-3 w-3 text-gray-400" />
                                                            {new Date(t.date).toLocaleDateString()}
                                                          </td>
                                                          <td className="px-3 py-2 text-gray-900">{t.type?.replace('_', ' ')}</td>
                                                          <td className={`px-3 py-2 text-right font-medium ${c.financier?.devise === 'USD' ? 'text-emerald-600' : 'text-gray-900'}`}>
                                                            {t.montant?.toLocaleString()} {c.financier?.devise === 'USD' ? '$' : 'G'}
                                                          </td>
                                                          <td className="px-3 py-2 text-right text-gray-500">{t.fait_par || '-'}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                ) : (
                                                  <div className="p-4 text-center text-xs text-gray-400 min-w-[500px]">{t('solide:agents.details.noTransactions')}</div>
                                                )}
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsBS;